import { hasTrackingConsent, trackDemoBooked, trackGtag } from './analytics';
import { onCalendlyBooking, openCalendlyPopup } from './calendly';
import {
  trackOpenAIAppointmentScheduled,
  trackOpenAICalendlyClick,
  trackOpenAIEmailClick,
} from './openaiPixel';

// Clicks that should keep the browser's own behaviour (new tab/window).
const isModifiedClick = (e) =>
  e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

/**
 * Calendly and mailto: links, handled by one delegated listener on the
 * document so links rendered later (mobile menu, route changes) are covered.
 *
 * Calendly links open the booking calendar as an overlay on our page instead
 * of leaving for calendly.com. The click itself is only recorded as intent
 * (GA4 `calendly_link_click`); the Google Ads conversion fires when Calendly
 * reports a completed booking.
 *
 * Tracking respects the cookie banner: gtag events only with consent, the
 * OpenAI pixel drops events itself while consent is denied. The calendar
 * itself opens for every visitor.
 *
 * @returns {() => void} Removes the listeners
 */
export const setupLinkTracking = () => {
  const handleClick = (e) => {
    if (!(e.target instanceof Element)) {
      return;
    }

    if (e.target.closest('a[href^="mailto:"]')) {
      trackOpenAIEmailClick();
      return;
    }

    const link = e.target.closest('a[href*="calendly.com"]');
    if (!link) {
      return;
    }

    const url = link.href;

    trackOpenAICalendlyClick();
    if (hasTrackingConsent()) {
      trackGtag('calendly_link_click', {
        event_category: 'Engagement',
        event_action: 'Calendly Link Click',
        event_label: url || 'Demo Booking',
      });
    }

    // Ctrl/Cmd/middle click, or an explicit "open at Calendly" link (e.g.
    // the fallback on /demo): let the browser open calendly.com as usual.
    if (isModifiedClick(e) || link.hasAttribute('data-calendly-direct')) {
      return;
    }

    e.preventDefault();
    openCalendlyPopup(url);
  };

  const stopBookingListener = onCalendlyBooking(({ bookingId }) => {
    trackDemoBooked(bookingId);
    trackOpenAIAppointmentScheduled(bookingId || undefined);
  });

  document.addEventListener('click', handleClick);
  return () => {
    document.removeEventListener('click', handleClick);
    stopBookingListener();
  };
};
